import React from "react";

import Conditional from "../../../common/conditional";
import CoverSheet from "../general/coverSheet";
import ETAnomalyPDF from "../et/ETAnomalyPDF";
import ETSummarySheet from "../et/etSummarySheet";
import HelideckPhotographicRecordPDF from "./helideckPhotographicRecordPDF";
import HelideckAnalysis from "./helideckAnalysisPage";
import PDFInfoSheet from "../../../themed/pdf/pdfInfoSheet";
import PDFPage from "../../../themed/pdf/pdfPage";
import PdfPhotoPage from "../../../themed/pdf/pdfPhotoPage";
import PDFSummarySheet from "./pdfSummarySheet";

import withPDF from "../../../hoc/withPDF";

import * as models from "../../../../utils/models";
import * as utils from "../../../../utils/utilities";

interface HelideckReportProps {
  anomalyData: models.VTEquipmentAnomalyData[];
  drySketchPhotos: models.PDFImageProps[];
  wetSketchPhotos: models.PDFImageProps[];
  dryTableData: any[];
  wetTableData: any[];
  tabularColumns: models.Column[];
  tabularSortColumn: models.SortColumn;
  coverSheetData: models.PDFCoverSheetProps;
  summarySheetData: models.HelideckSummaryOverview;
  visualReportData: models.ETSummarySheetData;
  drawingSheetPhotos: models.PDFImageProps[];
  photographicRecords: models.HelideckPhotoGraphicRecordProps;
  projectDetails: models.Project;
}

const HelideckReport = (props: HelideckReportProps): JSX.Element => {
  const standardCriteriaItems: models.CoverDataItem[] = [
    {
      label: "1. Helideck Friction Requirement",
      value: props.coverSheetData?.helideckFrictionRequirement,
    },
    {
      label: "2. Test Tyre Specification",
      value: props.coverSheetData?.testTyreSpec,
    },
    {
      label: "3. Road Marking Specification",
      value: props.coverSheetData?.roadMarking,
    },
    {
      label: "4. Material On Calibration Friction Measurement",
      value: props.coverSheetData?.materialCalibration,
    },
    {
      label: "5. Calibration Friction Measurement",
      value: props.coverSheetData?.calibrationFriction,
    },
    {
      label: "6. International Organization For Standardization",
      value: props.coverSheetData?.iso,
    },
  ];

  const referenceDocuments: models.CoverDataItem[] = [
    {
      label: "1. Drawing No",
      value: props.coverSheetData?.drawingNo,
    },
    {
      label: "2. Visual Testing Procedure",
      value: props.coverSheetData?.visualTesting,
    },
    {
      label: "3. Helideck Co-efficient of Friction Test Preocedure.",
      value: props.coverSheetData?.helideckCoefficient,
    },
  ];

  return (
    <React.Fragment>
      <PDFInfoSheet
        companyUrl={props.projectDetails.organisation.organisationLogo}
        documentNo={props.projectDetails.documentNumber}
        clientName={props.projectDetails.organisationName}
        facility={props.projectDetails.facilityName}
        reportTitle={props.projectDetails.projectTitle}
      />

      <PDFPage
        pageDetails={utils.getPageDetails("Cover Sheet")}
        projectDetails={props.projectDetails}
      >
        <CoverSheet
          overviewData={overviewData}
          referenceDocuments={referenceDocuments}
          standardCriteriaItems={standardCriteriaItems}
          note="Refer to work instruction for inspection requirement"
        />
      </PDFPage>

      <PDFPage
        pageDetails={utils.getPageDetails("Summary Sheet")}
        projectDetails={props.projectDetails}
      >
        <PDFSummarySheet {...props.summarySheetData} />
      </PDFPage>

      <PDFPage
        pageDetails={utils.getPageDetails("Visual Inspection Record")}
        projectDetails={props.projectDetails}
      >
        <ETSummarySheet {...props.visualReportData} />
      </PDFPage>

      <PDFPage
        pageDetails={utils.getPageDetails("Drawing Sheet")}
        projectDetails={props.projectDetails}
      >
        <PdfPhotoPage photos={props.drawingSheetPhotos} />
      </PDFPage>

      <PDFPage
        projectDetails={props.projectDetails}
        pageDetails={utils.getPageDetails(
          "Helideck Sketch - Dry Condition",
          "Dry Condition"
        )}
      >
        <PdfPhotoPage photos={props.drySketchPhotos} />
      </PDFPage>

      <HelideckAnalysis
        projectDetails={props.projectDetails}
        dryTableData={props.dryTableData}
        wetTableData={props.wetTableData}
      />

      <ETAnomalyPDF data={props.anomalyData} project={props.projectDetails} />

      <Conditional isVisible={!!props.photographicRecords}>
        <HelideckPhotographicRecordPDF
          {...props.photographicRecords}
          projectDetails={props.projectDetails}
        />
      </Conditional>
    </React.Fragment>
  );
};

const overviewData: models.CoverDataOverview[] = [
  { report: "1. Cover Sheet", required: "Yes", page: "2" },
  { report: "2. Summary Sheet", required: "Yes", page: "3" },
  { report: "3. Drawing Sheet", required: "Yes", page: "5" },
  {
    report: "4. Inspection Sketch - Dry Condition",
    required: "Yes",
    page: "6",
  },
  {
    report: "5. Inspection Sketch - Wet Condition",
    required: "Yes",
    page: "7",
  },
  { report: "6. Visual Inspection Report", required: "Yes", page: "8" },
  {
    report: "7. Helipad Data and Graph Sheet - Dry Condition",
    required: "Yes",
    page: "9",
  },
  {
    report: "8. Helipad Data and Graph Sheet - Wet Condition",
    required: "Yes",
    page: "10",
  },
  { report: "9. Photographic Record", required: "Yes", page: "11" },
];

export default withPDF(HelideckReport);
